import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Button from '../../Components/Button/Button';
import PreviousLandlordModal from '../../Components/PreviousLandlordModal/PreviousLandlordModal';
import { postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import {
  TENANT_DOC_FILE_LIMIT_SUBLINE,
  MAX_DOCUMENT_FILES_PER_CATEGORY,
  TENANT_DOC_ACCEPT_ATTR,
  MULTI_FILE_HINT_TEXT,
  ID_VERIFICATION_TYPE_OPTIONS,
  isValidTenantDocFile,
} from '../../constants/tenantDocuments';
import './TenantDocumentUpload.css';

const TenantDocumentUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.redirect;

  const getNextDestination = () => {
    if (redirectPath) {
      return redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
    }
    return '/tenant/properties';
  };

  const handleSkip = () => {
    navigate(getNextDestination(), { replace: true });
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedIdDocumentType, setSelectedIdDocumentType] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState({
    idVerificationFiles: [],
    bankStatements: [],
    landlordReference: null,
  });

  const mergeValidatedFiles = (fieldKey, picked, e) => {
    const invalidType = picked.find((f) => !isValidTenantDocFile(f));
    if (invalidType) {
      toast.error('Only PDF and image files (JPEG, PNG, WebP, GIF) are allowed.');
      e.target.value = '';
      return;
    }
    setUploadedFiles((prev) => {
      const current = prev[fieldKey];
      const room = MAX_DOCUMENT_FILES_PER_CATEGORY - current.length;
      if (room <= 0) {
        toast.error(`You can upload up to ${MAX_DOCUMENT_FILES_PER_CATEGORY} files for this section.`);
        return prev;
      }
      const toAdd = picked.slice(0, room);
      if (picked.length > room) {
        toast.info(`Only the first ${room} file(s) were added (max ${MAX_DOCUMENT_FILES_PER_CATEGORY} total).`);
      }
      return {
        ...prev,
        [fieldKey]: [...current, ...toAdd],
      };
    });
    e.target.value = '';
  };

  const handleIdVerificationUpload = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    mergeValidatedFiles('idVerificationFiles', picked, e);
  };

  const handleBankStatementsUpload = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    mergeValidatedFiles('bankStatements', picked, e);
  };

  const removeIdVerificationAt = (index) => {
    setUploadedFiles((prev) => ({
      ...prev,
      idVerificationFiles: prev.idVerificationFiles.filter((_, i) => i !== index),
    }));
  };

  const removeBankStatementAt = (index) => {
    setUploadedFiles((prev) => ({
      ...prev,
      bankStatements: prev.bankStatements.filter((_, i) => i !== index),
    }));
  };

  const getFileName = (file) => {
    if (!file) return null;
    // Truncate long file names to keep UI clean
    const maxLength = 30;
    if (file.name.length > maxLength) {
      const extension = file.name.split('.').pop();
      const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
      return `${nameWithoutExt.substring(0, maxLength - extension.length - 4)}...${extension}`;
    }
    return file.name;
  };

  const handleContinue = async () => {
    if (uploadedFiles.idVerificationFiles.length === 0 || uploadedFiles.bankStatements.length === 0) {
      setErrors({
        submit: 'At least one ID verification file and one bank statement are required.',
      });
      toast.error('Please upload at least one ID verification file and one bank statement.');
      return;
    }

    if (!selectedIdDocumentType) {
      const msg = 'Please select which type of ID document you are uploading.';
      setErrors({ submit: msg });
      toast.error(msg);
      return;
    }

    const checkType = (file, name) => {
      if (file && !isValidTenantDocFile(file)) {
        setErrors({ submit: `${name} must be a PDF or allowed image type.` });
        toast.error(`${name} must be a PDF or allowed image type.`);
        return false;
      }
      return true;
    };

    for (let i = 0; i < uploadedFiles.idVerificationFiles.length; i++) {
      if (!checkType(uploadedFiles.idVerificationFiles[i], `ID verification ${i + 1}`)) return;
    }
    for (let i = 0; i < uploadedFiles.bankStatements.length; i++) {
      if (!checkType(uploadedFiles.bankStatements[i], `Bank statement ${i + 1}`)) return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const formData = new FormData();

      let docIndex = 0;

      uploadedFiles.idVerificationFiles.forEach((file, i) => {
        formData.append(`documents[${docIndex}][file]`, file);
        formData.append(`documents[${docIndex}][type]`, selectedIdDocumentType);
        formData.append(`documents[${docIndex}][side]`, i % 2 === 0 ? 'front_side' : 'back_side');
        docIndex++;
      });
      uploadedFiles.bankStatements.forEach((file, i) => {
        formData.append(`documents[${docIndex}][file]`, file);
        formData.append(`documents[${docIndex}][type]`, 'BANK_STATEMENT');
        formData.append(`documents[${docIndex}][side]`, i % 2 === 0 ? 'front_side' : 'back_side');
        docIndex++;
      });

      // Add landlord reference info if provided
      if (uploadedFiles.landlordReference) {
        if (uploadedFiles.landlordReference.name) {
          formData.append('previousLandlordName', uploadedFiles.landlordReference.name);
        }
        if (uploadedFiles.landlordReference.phoneNumber) {
          formData.append('previousLandlordPhone', uploadedFiles.landlordReference.phoneNumber);
        }
        if (uploadedFiles.landlordReference.email) {
          formData.append('previousLandlordEmail', uploadedFiles.landlordReference.email);
        }
      }

      // Call API with multipart/form-data
      // Note: Axios automatically sets Content-Type to multipart/form-data with boundary when FormData is used
      const response = await postApi('/auth/upload-tenant-documents/v2', formData);

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        toast.success(response?.message || 'Documents uploaded successfully');
        setIsUploaded(true);
      } else {
        const errorMessage = response?.message || 'Failed to upload documents. Please try again.';
        setErrors({ submit: errorMessage });
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to upload documents. Please try again.';
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show success screen after successful upload
  if (isUploaded) {
    return (
      <div className="verification-success-page">
        <div className="verification-success-container">
          <div className="verification-success-card">
            <div className="verification-success-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
              </svg>
            </div>
            <h1 className="verification-success-title">Welcome to Clear Rent</h1>
            <p className="verification-success-message">
              You've successfully uploaded your documents.
            </p>
            <Button 
              variant="success" 
              className="verification-success-button"
              onClick={() => navigate(getNextDestination(), { replace: true })}
            >
              Continue to properties
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-upload-page">
      <div className="tenant-upload-container">
        {/* Left Side - Branding */}
        <div className="tenant-upload-info">
          <div className="tenant-upload-logo">
            <Link to="/">
              <img src={window.getPublicUrl("/assets/images/Logo.png")} alt="ClearRent" className="tenant-upload-logo-img" />
            </Link>
          </div>
          <h1 className="tenant-upload-info-title">
            Apply for properties with ease
          </h1>
          <p className="tenant-upload-info-subtitle">
            Create your account and apply for properties quickly and securely.
          </p>
          <ul className="tenant-upload-features">
            <li>
              <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="tenant-upload-feature-icon" />
              <span>Quick application process</span>
            </li>
            <li>
              <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="tenant-upload-feature-icon" />
              <span>Secure document upload & verification</span>
            </li>
            <li>
              <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="tenant-upload-feature-icon" />
              <span>Automated reference checks</span>
            </li>
          </ul>
        </div>

        {/* Right Side - Upload Form */}
        <div className="tenant-upload-form-section">
          <div className="tenant-upload-header">
            <div className="tenant-upload-icon">
            </div>
            <h1 className="tenant-upload-title">Upload Documents</h1>
            <p className="tenant-upload-subtitle">
              Required for income and identity verification. You can skip and upload later from your profile.
            </p>
          </div>

        <div className="tenant-upload-cards">
          {/* Id Verification Card */}
          <div className="tenant-upload-card">
            <div className="tenant-upload-card-content">
              <div className="tenant-upload-card-info">
                <h3 className="tenant-upload-card-title">Id Verification</h3>
                <p className="tenant-upload-card-subtitle">{TENANT_DOC_FILE_LIMIT_SUBLINE}</p>
                <fieldset className="tenant-upload-id-type-fieldset">
                  <legend className="tenant-upload-id-type-legend">Document type</legend>
                  <div className="tenant-upload-id-type-radios" role="radiogroup" aria-label="ID document type">
                    {ID_VERIFICATION_TYPE_OPTIONS.map(({ value, label }) => (
                      <label key={value} className="tenant-upload-id-type-option">
                        <input
                          type="radio"
                          name="idDocumentType"
                          value={value}
                          checked={selectedIdDocumentType === value}
                          onChange={() => setSelectedIdDocumentType(value)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                {uploadedFiles.idVerificationFiles.length > 0 && (
                  <ul className="tenant-upload-file-list">
                    {uploadedFiles.idVerificationFiles.map((file, idx) => (
                      <li key={`${file.name}-${idx}-${file.size}`} className="tenant-upload-file-item">
                        <span>{getFileName(file)}</span>
                        <button
                          type="button"
                          className="tenant-upload-remove-file"
                          onClick={() => removeIdVerificationAt(idx)}
                          aria-label={`Remove ${file.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="tenant-upload-card-action" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label
                  htmlFor="id-verification-upload"
                  className="tenant-upload-button"
                  style={{
                    opacity: uploadedFiles.idVerificationFiles.length >= MAX_DOCUMENT_FILES_PER_CATEGORY ? 0.6 : 1,
                    pointerEvents:
                      uploadedFiles.idVerificationFiles.length >= MAX_DOCUMENT_FILES_PER_CATEGORY ? 'none' : 'auto',
                  }}
                >
                  {uploadedFiles.idVerificationFiles.length === 0
                    ? 'Upload files'
                    : uploadedFiles.idVerificationFiles.length >= MAX_DOCUMENT_FILES_PER_CATEGORY
                      ? `Maximum (${MAX_DOCUMENT_FILES_PER_CATEGORY}) reached`
                      : 'Add files'}
                  <input
                    id="id-verification-upload"
                    type="file"
                    multiple
                    accept={TENANT_DOC_ACCEPT_ATTR}
                    onChange={handleIdVerificationUpload}
                    disabled={uploadedFiles.idVerificationFiles.length >= MAX_DOCUMENT_FILES_PER_CATEGORY}
                    style={{ display: 'none' }}
                  />
                </label>
                {uploadedFiles.idVerificationFiles.length > 0 && (
                  <div className="tenant-upload-status-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#1A5D3A"/>
                      <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bank Statements Card */}
          <div className="tenant-upload-card">
            <div className="tenant-upload-card-content">
              <div className="tenant-upload-card-info">
                <h3 className="tenant-upload-card-title">Bank Statements</h3>
                <p className="tenant-upload-card-subtitle">
                  Last 2 to 3 months
                  {MULTI_FILE_HINT_TEXT}
                </p>
                {uploadedFiles.bankStatements.length > 0 && (
                  <ul className="tenant-upload-file-list">
                    {uploadedFiles.bankStatements.map((file, idx) => (
                      <li key={`${file.name}-${idx}-${file.size}`} className="tenant-upload-file-item">
                        <span>{getFileName(file)}</span>
                        <button
                          type="button"
                          className="tenant-upload-remove-file"
                          onClick={() => removeBankStatementAt(idx)}
                          aria-label={`Remove ${file.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="tenant-upload-card-action" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label
                  htmlFor="bank-statements-upload"
                  className="tenant-upload-button"
                  style={{
                    opacity: uploadedFiles.bankStatements.length >= MAX_DOCUMENT_FILES_PER_CATEGORY ? 0.6 : 1,
                    pointerEvents:
                      uploadedFiles.bankStatements.length >= MAX_DOCUMENT_FILES_PER_CATEGORY ? 'none' : 'auto',
                  }}
                >
                  {uploadedFiles.bankStatements.length === 0
                    ? 'Upload statements'
                    : uploadedFiles.bankStatements.length >= MAX_DOCUMENT_FILES_PER_CATEGORY
                      ? `Maximum (${MAX_DOCUMENT_FILES_PER_CATEGORY}) reached`
                      : 'Add files'}
                  <input
                    id="bank-statements-upload"
                    type="file"
                    multiple
                    accept={TENANT_DOC_ACCEPT_ATTR}
                    onChange={handleBankStatementsUpload}
                    disabled={uploadedFiles.bankStatements.length >= MAX_DOCUMENT_FILES_PER_CATEGORY}
                    style={{ display: 'none' }}
                  />
                </label>
                {uploadedFiles.bankStatements.length > 0 && (
                  <div className="tenant-upload-status-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#1A5D3A"/>
                      <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Previous Landlord References Card */}
          <div className="tenant-upload-card">
            <div className="tenant-upload-card-content">
              <div className="tenant-upload-card-info">
                <h3 className="tenant-upload-card-title">Previous Landlord References</h3>
                <p className="tenant-upload-card-subtitle">Contact info or letters</p>
              </div>
              <div className="tenant-upload-card-action">
                <button
                  className="tenant-upload-button tenant-upload-fill-form-button"
                  onClick={() => setIsModalOpen(true)}
                >
                  Fill Form
                </button>
                {uploadedFiles.landlordReference && (
                  <div className="tenant-upload-status-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#1A5D3A"/>
                      <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

          <div className="tenant-upload-footer">
            {errors.submit && (
              <div className="error-message-container has-error" style={{ marginBottom: '1rem', width: '100%' }}>
                <span className="error-message">{errors.submit}</span>
              </div>
            )}
            <div className="tenant-upload-footer-actions">
              <Button
                type="button"
                variant="outline"
                size="large"
                className="tenant-upload-skip-button"
                onClick={handleSkip}
                disabled={isLoading}
              >
                Skip for now
              </Button>
              <Button
                variant="success"
                size="large"
                className="tenant-upload-continue-button"
                onClick={handleContinue}
                disabled={isLoading}
              >
                {isLoading ? 'Uploading...' : 'Continue'}
              </Button>
            </div>
            <p className="tenant-upload-disclaimer">
              By proceeding, you agree to our Terms of Service and consent to Automated screening.
            </p>
          </div>
        </div>
      </div>

      <PreviousLandlordModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(data) => {
          setUploadedFiles((prev) => ({ ...prev, landlordReference: data }));
          setIsModalOpen(false);
        }}
      />
    </div>
  );
};

export default TenantDocumentUpload;
